import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { useMemo, useState } from "react";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ProjectGrid } from "@/components/site/ProjectGrid";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { CtaSplit } from "@/components/site/CtaSplit";
import { Ruler, ArrowRight, X, Layers, MapPin } from "lucide-react";
import { mediaQuery } from "@/lib/media-queries";
import { publishedProjectsQuery } from "@/lib/projects-queries";
import { projectTypeLabel, projectTypes } from "@/content/project-types";
import type { ResponsiveVariant } from "@/components/site/ResponsiveImage";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { breadcrumbJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/realisaties";
const SLEUTEL = "realisaties";

const CODE_META_TITLE = "Realisaties zwembaden Limburg | Gopro Projects";
const CODE_META_DESCRIPTION =
  "Echte realisaties van Gopro Projects: bouwkundige zwembaden met isolatie, polyester bekleding en OCEA rolluiken in Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/realisaties/")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => {
    const [assets, projects, page] = await Promise.all([
      context.queryClient.ensureQueryData(mediaQuery("realisaties")),
      context.queryClient.ensureQueryData(publishedProjectsQuery(params.lang)),
      context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
    ]);
    return { page, heroFallback: { src: projects[0]?.hoofdbeeld?.url ?? assets[0]?.url ?? "", variants: projects[0]?.hoofdbeeld?.variants ?? assets[0]?.variants } };
  },
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), loaderData?.heroFallback ?? { src: "" });
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: "Echte projecten: bouwkundige zwembaden, polyester bekleding en zwembadrolluiken.",
        },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("realisaties", "Gerealiseerd bouwkundig zwembad 10 x 3 m met lichtgrijze polyester bekleding"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [faqJsonLd(faqs.realisaties), breadcrumbJsonLd("Realisaties", PATH)],
    };
  },
  component: RealisatiesPage,
});

type Photo = { src: string; width: number; height: number; alt: string; variants?: ResponsiveVariant[] };

const fallbackProject = {
  title: "Bouwkundig zwembad 10 x 3 m",
  type: "Nieuwbouw",
  dimensions: "10 x 3 m",
  intro:
    "Een bouwkundig zwembad van 10 op 3 meter, volledig geïsoleerd en afgewerkt met een lichtgrijze polyester zwembadbekleding. Het onderliggende zwembadrolluik met solar lamellen is netjes weggewerkt in de plage, die tegelijk dienstdoet als zitbank.",
  specs: [
    "Bouwkundige kuip, volledig geïsoleerd",
    "Lichtgrijze polyester zwembadbekleding",
    "Onderliggend rolluik met solar lamellen",
    "Rolluikkast verwerkt in de plage / zitbank",
    "Boordstenen en terras in natuursteenlook",
    "Sfeerverlichting in bad en terras",
  ],
};

function RealisatiesPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("alle");
  const [gemeenteFilter, setGemeenteFilter] = useState<string>("alle");
  const { data: assets } = useSuspenseQuery(mediaQuery("realisaties"));
  const { data: projects } = useSuspenseQuery(publishedProjectsQuery(lang));

  const photos: Photo[] = assets.map((asset) => ({
    src: asset.url,
    width: asset.width ?? 1200,
    height: asset.height ?? 1600,
    alt: asset.alt,
    variants: asset.variants,
  }));
  const hero = c.section("hero");
  const heroImage = hero?.mediaUrl ?? projects[0]?.hoofdbeeld?.url ?? photos[0]?.src ?? "";
  const heroPhoto = heroDelivery(hero, { src: heroImage, variants: projects[0]?.hoofdbeeld?.variants ?? photos[0]?.variants });
  const heroAlt = c.veld(
    "hero",
    "alt",
    projects[0]?.hoofdbeeld?.alt ?? "Bouwkundig zwembad 10 x 3 m met sfeerverlichting",
  );

  const gemeenten = useMemo(
    () => [...new Set(projects.map((project) => project.gemeente).filter((value) => value !== ""))].sort(),
    [projects],
  );
  const availableTypes = useMemo(
    () => projectTypes.filter((type) => projects.some((project) => project.typeWerk === type.id)),
    [projects],
  );
  const visible = projects.filter(
    (project) =>
      (typeFilter === "alle" || project.typeWerk === typeFilter) &&
      (gemeenteFilter === "alle" || project.gemeente === gemeenteFilter),
  );

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Onze realisaties")}
        subtitle={c.rijkInline("hero",
          "Echte projecten van Gopro Projects: bouwkundige zwembaden van a tot z, met eigen technieken, isolatie en afdekking.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={heroAlt}
        cta={{
          label: c.veld("hero", "knopTekst", "Start uw project"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
      />

      <Section tone="navy" compact>
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <Overline onDark>{c.veld("opbouw-band", "overline", "Opbouw")}</Overline>
            <h2 className="mt-3 font-display text-xl font-semibold">
              {c.veld("opbouw-band", "titel", "Van A tot Z: zo bouwen wij")}
            </h2>
            <p className="mt-2 max-w-2xl text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("opbouw-band",
                "Volg de volledige opbouw van een bouwkundig zwembad, fase per fase in beeld.",
              )}
            </p>
          </div>
          <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
            <LocaleLink to={c.veld("opbouw-band", "knopLink", "/opbouw-zwembad") as PublicPath}>
              {c.veld("opbouw-band", "knopTekst", "Bekijk de volledige opbouw")}{" "}
              <ArrowRight className="ml-2 h-4 w-4" />
            </LocaleLink>
          </Button>
        </div>
      </Section>

      <Section tone="light">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-16">
          <div>
            <Overline>{c.veld("ons-werk", "overline", "Ons werk")}</Overline>
            <h2 className="mt-4 display-section">
              {c.veld("ons-werk", "titel", "Wat voor zwembaden bouwen en renoveren wij?")}
            </h2>
            <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("ons-werk",
                "Gopro Projects realiseert bouwkundige zwembaden met 3 cm Recticel isolatie en een polyester of vinylester bekleding, en renoveert bestaande bouwkundige en polyester monoblock zwembaden. Alle projecten hieronder zijn eigen realisaties, geen stockbeelden.",
              )}
            </p>
          </div>
          <div className="border-l border-border pl-6 lg:pl-12">
            <p className="text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("werkgebied",
                "Wij werken vanuit Maasmechelen in heel Limburg — onder meer Genk, Hasselt, Bilzen, Lanaken, Dilsen-Stokkem, Bree en Tongeren — en in de Nederlandse grensstreek rond Maastricht, Sittard-Geleen en Roermond. Bij elk project verzorgen wij het volledige traject: grondwerken, kuip, isolatie, bekleding, filtratie, verwarming, afdekking, boordstenen en desgewenst het terras.",
              )}
            </p>
          </div>
        </div>
      </Section>

      {projects.length > 0 ? (
        <Section tone="mist">
          <Overline>{c.veld("projecten", "overline", "Projecten")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("projecten", "titel", "Onze gerealiseerde projecten")}</h2>

          <div className="mt-8 flex flex-wrap items-end gap-6">
            <fieldset className="flex flex-wrap items-center gap-2">
              <legend className="mb-2 text-sm font-semibold text-on-surface-variant">Soort werk</legend>
              <Button
                type="button"
                size="sm"
                variant={typeFilter === "alle" ? "default" : "outline"}
                aria-pressed={typeFilter === "alle"}
                onClick={() => setTypeFilter("alle")}
              >
                Alles
              </Button>
              {availableTypes.map((type) => (
                <Button
                  key={type.id}
                  type="button"
                  size="sm"
                  variant={typeFilter === type.id ? "default" : "outline"}
                  aria-pressed={typeFilter === type.id}
                  onClick={() => setTypeFilter(type.id)}
                >
                  {type.label}
                </Button>
              ))}
            </fieldset>

            {gemeenten.length > 1 && (
              <div>
                <label htmlFor="gemeente-filter" className="mb-2 block text-sm font-semibold text-on-surface-variant">
                  Gemeente
                </label>
                <select
                  id="gemeente-filter"
                  value={gemeenteFilter}
                  onChange={(event) => setGemeenteFilter(event.target.value)}
                  className="h-10 rounded border border-border bg-background px-3 text-sm focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <option value="alle">Alle gemeenten</option>
                  {gemeenten.map((gemeente) => (
                    <option key={gemeente} value={gemeente}>
                      {gemeente}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {visible.length === 0 ? (
            <p className="mt-10 text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("leeg", "Er zijn geen realisaties die aan deze keuze voldoen. Kies een andere filter.")}
            </p>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((project) => (
                <LocaleLink
                  key={project.id}
                  to="/realisaties/$slug"
                  params={{ slug: project.slug }}
                  className="group block overflow-hidden rounded-lg border border-border bg-background focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-surface">
                    {project.hoofdbeeld ? (
                      <ResponsiveImage
                        src={project.hoofdbeeld.url}
                        alt={project.hoofdbeeld.alt}
                        variants={project.hoofdbeeld.variants}
                        fallbackSrc={project.hoofdbeeld.fallbackUrl}
                        focal={project.hoofdbeeld.focal}
                        decorative={project.hoofdbeeld.decorative}
                        placeholderColor={project.hoofdbeeld.placeholderColor}
                        width={project.hoofdbeeld.width ?? 1024}
                        height={project.hoofdbeeld.height ?? 768}
                        className="transition-transform duration-300 ease-out group-hover:scale-105"
                      />
                    ) : null}
                    <div className="absolute inset-x-0 bottom-0 bg-[linear-gradient(transparent,color-mix(in_oklab,var(--primary)_85%,transparent))] px-5 pb-4 pt-16">
                      <p className="font-display text-lg font-semibold text-primary-foreground">{project.titel}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 px-5 py-4 text-sm text-on-surface-variant">
                    <span className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-brand-deep" strokeWidth={1.75} />
                      {projectTypeLabel(project.typeWerk)}
                    </span>
                    {project.gemeente && (
                      <span className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-brand-deep" strokeWidth={1.75} />
                        {project.gemeente}
                      </span>
                    )}
                  </div>
                </LocaleLink>
              ))}
            </div>
          )}
        </Section>
      ) : (
        <Section tone="mist">
          <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
            <div className="lg:col-span-2">
              <Overline>{fallbackProject.type}</Overline>
              <h2 className="mt-4 display-section">{fallbackProject.title}</h2>
              <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">{fallbackProject.intro}</p>
              <div className="mt-6 flex flex-wrap gap-6 text-sm text-on-surface-variant">
                <span className="flex items-center gap-2">
                  <Ruler className="h-5 w-5 text-brand-deep" strokeWidth={1.75} /> {fallbackProject.dimensions}
                </span>
                <span className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-brand-deep" strokeWidth={1.75} /> Geïsoleerd met polyester bekleding
                </span>
              </div>
            </div>
            <ul className="divide-y divide-border border-y border-border text-sm text-on-surface-variant lg:mt-10">
              {fallbackProject.specs.map((spec) => (
                <li key={spec} className="py-3 leading-6">
                  {spec}
                </li>
              ))}
            </ul>
          </div>

          <ProjectGrid
            className="mt-12"
            items={photos.map((photo) => ({
              src: photo.src,
              alt: photo.alt,
              variants: photo.variants ?? [],
              onSelect: () => setLightbox(photo),
            }))}
          />
        </Section>
      )}

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Sluiten"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full bg-background/90 p-2 text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            width={lightbox.width}
            height={lightbox.height}
            className="max-h-[85vh] w-auto max-w-full object-contain"
          />
        </div>
      )}

      <CtaSplit
        overline={c.veld("cta", "overline", "Uw project")}
        title={c.veld("cta", "titel", "Ook zo'n resultaat in uw tuin?")}
        text={c.rijkInline("cta", "Vertel ons uw wensen en ontvang een vrijblijvende offerte op maat.")}
        imageSrc={heroImage}
        imageAlt={heroAlt}
        ctaLabel={c.veld("cta", "knopTekst", "Offerte aanvragen")}
      />
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.realisaties} />
    </div>
  );
}
